type GoogleMapsNs = {
  maps: {
    Map: new (el: HTMLElement, opts: Record<string, unknown>) => GoogleMap;
    Marker: new (opts: Record<string, unknown>) => GoogleMarker;
    Geocoder: new () => GoogleGeocoder;
    event: { trigger: (target: unknown, name: string) => void; clearInstanceListeners?: (target: unknown) => void };
    places: {
      Autocomplete: new (input: HTMLInputElement, opts: Record<string, unknown>) => GoogleAutocomplete;
      AutocompleteService: new () => GoogleAutocompleteService;
      AutocompleteSessionToken?: new () => unknown;
      PlacesService: new (el: HTMLElement) => GooglePlacesService;
    };
  };
};

export type GoogleMap = {
  setCenter: (latLng: { lat: number; lng: number }) => void;
  setZoom: (zoom: number) => void;
  addListener: (event: string, handler: (e: { latLng: { lat: () => number; lng: () => number } }) => void) => void;
};

export type GoogleMarker = {
  setPosition: (latLng: { lat: number; lng: number }) => void;
  setMap: (map: GoogleMap | null) => void;
  addListener: (event: string, handler: () => void) => void;
  getPosition: () => { lat: () => number; lng: () => number } | null;
};

type GoogleGeocoder = {
  geocode: (
    req: { location: { lat: number; lng: number } },
    cb: (results: GoogleGeocodeResult[] | null, status: string) => void
  ) => void;
};

export type GoogleAutocomplete = {
  getPlace: () => GooglePlaceDetails;
  addListener: (event: string, handler: () => void) => unknown;
  setBounds?: (bounds: { north: number; south: number; east: number; west: number }) => void;
};

type GoogleAutocompleteService = {
  getPlacePredictions: (
    req: {
      input: string;
      componentRestrictions?: { country: string };
      locationBias?: { radius: number; center: { lat: number; lng: number } };
      sessionToken?: unknown;
    },
    cb: (predictions: GooglePrediction[] | null, status: string) => void
  ) => void;
};

export const INDIA_PLACE_COUNTRY = 'in';
const BENGALURU = { lat: 12.9716, lng: 77.5946 };

export function indiaPlaceBiasBounds(bias?: { lat: number; lng: number }): {
  north: number;
  south: number;
  east: number;
  west: number;
} {
  const center =
    bias && Number.isFinite(bias.lat) && Number.isFinite(bias.lng) ? bias : BENGALURU;
  return {
    north: center.lat + 0.45,
    south: center.lat - 0.45,
    east: center.lng + 0.45,
    west: center.lng - 0.45,
  };
}

export function indiaPlaceSearchOptions(bias?: { lat: number; lng: number }): Record<string, unknown> {
  return {
    componentRestrictions: { country: INDIA_PLACE_COUNTRY },
    fields: ['place_id', 'geometry', 'formatted_address', 'address_components', 'name'],
    bounds: indiaPlaceBiasBounds(bias),
    strictBounds: false,
  };
}

type GooglePlacesService = {
  getDetails: (
    req: { placeId: string; fields: string[]; sessionToken?: unknown },
    cb: (place: GooglePlaceDetails | null, status: string) => void
  ) => void;
};

export type GooglePrediction = {
  place_id: string;
  description: string;
  structured_formatting?: { main_text?: string; secondary_text?: string };
};

export type GoogleAddressComponent = {
  long_name: string;
  short_name: string;
  types: string[];
};

export type GooglePlaceDetails = {
  place_id?: string;
  formatted_address?: string;
  name?: string;
  geometry?: { location: { lat: () => number; lng: () => number } };
  address_components?: GoogleAddressComponent[];
};

export type GoogleGeocodeResult = {
  formatted_address?: string;
  place_id?: string;
  address_components?: GoogleAddressComponent[];
  geometry?: { location: { lat: () => number; lng: () => number } };
};

let mapsLoad: Promise<GoogleMapsNs> | null = null;
let loadedMaps: GoogleMapsNs | null = null;
let mapsAuthFailed = false;

export function isGoogleMapsBlocked(): boolean {
  return mapsAuthFailed;
}

function watchAuthFailure() {
  const w = window as unknown as { gm_authFailure?: () => void };
  const previous = w.gm_authFailure;
  w.gm_authFailure = () => {
    mapsAuthFailed = true;
    mapsLoad = null;
    loadedMaps = null;
    if (typeof previous === 'function') {
      previous();
    }
  };
}

export function googleMapsNs(): GoogleMapsNs | null {
  if (mapsAuthFailed) {
    return null;
  }
  if (loadedMaps?.maps?.places) {
    return loadedMaps;
  }
  const g = (window as unknown as { google?: GoogleMapsNs }).google;
  if (g?.maps?.places) {
    loadedMaps = g;
    return g;
  }
  return null;
}

export function loadGoogleMaps(apiKey: string): Promise<GoogleMapsNs> {
  watchAuthFailure();
  if (mapsAuthFailed) {
    return Promise.reject(new Error('Google Maps is not allowed on this origin'));
  }
  const existingNs = googleMapsNs();
  if (existingNs) {
    return Promise.resolve(existingNs);
  }
  if (mapsLoad) {
    return mapsLoad;
  }
  const key = apiKey.trim();
  if (!key) {
    return Promise.reject(new Error('Google Maps is not configured'));
  }
  mapsLoad = new Promise((resolve, reject) => {
    const finish = () => {
      const ns = googleMapsNs();
      if (ns) {
        resolve(ns);
        return true;
      }
      return false;
    };
    if (finish()) {
      return;
    }
    const src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&libraries=places&region=IN&language=en`;
    const existing = document.querySelector(`script[src^="https://maps.googleapis.com/maps/api/js"]`) as HTMLScriptElement | null;
    const waitForPlaces = () => {
      if (finish()) {
        return;
      }
      let attempts = 0;
      const timer = window.setInterval(() => {
        attempts += 1;
        if (finish() || attempts > 40) {
          window.clearInterval(timer);
          if (!googleMapsNs()) {
            mapsLoad = null;
            reject(new Error('Google Maps Places failed to load'));
          }
        }
      }, 50);
    };
    if (existing) {
      existing.addEventListener('load', waitForPlaces);
      existing.addEventListener('error', () => {
        mapsLoad = null;
        reject(new Error('Google Maps failed to load'));
      });
      waitForPlaces();
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = waitForPlaces;
    script.onerror = () => {
      mapsLoad = null;
      reject(new Error('Google Maps failed to load'));
    };
    document.head.appendChild(script);
  });
  return mapsLoad;
}

export function placesHost(): HTMLDivElement {
  let el = document.getElementById('gmaps-places-host') as HTMLDivElement | null;
  if (!el) {
    el = document.createElement('div');
    el.id = 'gmaps-places-host';
    el.style.display = 'none';
    document.body.appendChild(el);
  }
  return el;
}
